import Service from '@ember/service';
import { getOwner } from '@ember/application';
import { inject as service } from '@ember/service';
import { A as emberArray } from '@ember/array';
import { all, hash, resolve } from 'rsvp';

export default class CurrentUserService extends Service {
  @service store;

  @service session;

  @service router;

  // @service toast;

  @service currentEmpresa;

  user = null;

  load() {
    let token = this.session.data.authenticated.token;

    if (!token) {
      throw Error('User not authenticated.');
    }

    let authenticator = getOwner(this).lookup('authenticator:jwt');
    let [username, codempresa] = authenticator
      .getTokenData(token)
      .username.split('@', 2);

    if (!username) {
      throw Error('Username not found from session data.');
    }

    // this.loader.setLoading(true);

    return this.store
      .query('vf-user', {
        filter: {
          coduser: {
            exact: username,
          },
          codempresa: {
            exact: codempresa,
          },
        },
        include: 'perfil',
      })
      .then((result) => {
        if (0 === result.length) {
          throw new Error(
            `Got no record for 'usuario' with username '${username}'`,
          );
        }

        if (1 !== result.length) {
          throw new Error(
            `Got more than one record for 'usuario' with username '${username}'`,
          );
        }

        this.user = result[0];
      })
      .catch((error) => {
        // this.toast.error('No es posible acceder en este momento.');

        throw error;
      });
    // .finally(() => this.loader.setLoading(false));
  }

  /**
   * Obtiene los menús y permisos asociados al perfil del usuario actual. También
   * carga los menús superiores a los que el perfil no tiene acceso, pero son
   * requeridos para mostrar los menús hijos a los que sí tiene acceso.
   *
   * @return Promise
   */
  fetchPerfilData() {
    if (!this.user) {
      throw Error('User not loaded yet.');
    }

    if (!this.user.belongsTo('perfil').id()) {
      this.permissions = [];
      // this.toast.error('No tiene un perfil asignado.');

      return resolve(null);
    }

    let menuIds = this.user.get('perfil').hasMany('menus').ids();
    let permissionIds = this.user.get('perfil').hasMany('permisos').ids();

    let menusPromise = resolve([]);
    let permissionsPromise = resolve([]);

    if (menuIds.length) {
      menusPromise = this.store.query('menu', {
        filter: {
          id: menuIds,
          activo: 1,
        },
      });
    }

    if (permissionIds.length) {
      permissionsPromise = this.store.query('permiso', {
        filter: {
          id: permissionIds,
          activo: 1,
        },
      });
    }

    return hash({
      menus: menusPromise,
      permissions: permissionsPromise,
    }).then(({ menus, permissions }) => {
      this.permissions = permissions;

      let finalMenus = emberArray([...menus]);
      // Listado de promesas que cargan todos los menús superiores. Un menú superior
      // está asignado al usuario solo si todos sus hijos están también asignados.
      let getParentsPromise = menus.map((menu) => this._getParentMenus(menu));

      return all(getParentsPromise).then(() => {
        // Una vez cargados todos los menús, localizar los menús superiores
        // que no se encuentran en el listado de menús del perfil y agregarlos.
        menus.forEach((currentMenu) => {
          while (currentMenu.get('superior.id')) {
            let superior = currentMenu.get('superior');

            if (!finalMenus.includes(superior)) {
              finalMenus.pushObject(superior);
            }

            currentMenu = superior;
          }
        });

        this.menus = finalMenus;

        if (!this.menus.length) {
          // this.toast.warning('Su perfil no tiene asignado ningún acceso.');
        }
      });
    });
  }

  /**
   * Carga todos los menús superiores de un menú, recursivamente en una sola promesa.
   *
   * @param  Menu menu
   * @return Promise
   */
  _getParentMenus(menu) {
    if (!menu.belongsTo('superior').id()) {
      return resolve(null);
    }

    return menu
      .belongsTo('superior')
      .load()
      .then((superior) => this._getParentMenus(superior));
  }

  _routeExists(routeName) {
    try {
      return Boolean(this.router.urlFor(routeName));
    } catch (error) {
      return false;
    }
  }

  _menuRouteExists(menu) {
    // If the menu doesn't have a route, ignore.
    if (!menu.get('ruta') || !menu.get('ruta').trim()) {
      return true;
    }

    menu.set('routeExists', this._routeExists(menu.get('ruta').trim()));

    return menu.get('routeExists');
  }

  _sortByOrden(el1, el2) {
    return el1.get('orden') - el2.get('orden');
  }

  _haveChildrenWithRoute(menu) {
    if (!menu.children.length) {
      return false;
    }

    return menu.children.reduce(
      (prevValue, menu) => prevValue || menu.get('routeExists') || this._haveChildrenWithRoute(menu),
      false
    );
  }

  /**
   * Constuye el árbol de menús para el sidebar.
   *
   * @return void
   */
  setupMenuTree() {
    if (!this.menus) {
      throw Error('Menus not loaded yet.');
    }

    let sidebarMenus = this.menus
      .filter((el) => !el.get('superior.id'))
      .filter(this._menuRouteExists.bind(this))
      .sort(this._sortByOrden);

    let childMenus = this.menus
      .filter((el) => el.get('superior.id'))
      .filter(this._menuRouteExists.bind(this))
      .sort(this._sortByOrden);

    sidebarMenus.forEach((menu) => this._setupMenuChildren(menu, childMenus));

    this.sidebarMenus = sidebarMenus;

    // WARNING: For debugging only
    // sidebarMenus.forEach((menu) => this._printMenuTree(menu));
  }

  /**
   * Localiza los menús hijos de un menú y se lo setea en la propiedad "children"
   * como un array. Ejecuta el proceso recursivamente sobre cada menú hijo.
   *
   * @param  Menu menu
   * @param  Menu[] childMenus
   * @return void
   */
  _setupMenuChildren(menu, childMenus) {
    menu.children = emberArray([]);

    let menuChildren = childMenus
      .filter((el) => el.get('superior.id') === menu.get('id'))
      .sort(this._sortByOrden);

    menuChildren.forEach((childMenu) => {
      menu.children.push(childMenu);

      this._setupMenuChildren(childMenu, childMenus);
    });

    menu.haveValidChildren = this._haveChildrenWithRoute(menu);
  }

  /**
   * Imprime en la consola el árbol de menús del menú recibido, recursivamente.
   * Los logs están comentados para evitar errores del linter. Hay que descomentarlos.
   *
   * @param  Menu menu
   * @return void
   */
  _printMenuTree(menu) {
    // console.group(menu.codigo);
    menu.children.forEach((childMenu) => {
      this._printMenuTree(childMenu);
    });
    // console.groupEnd();
  }

  /**
   * letruye un objeto plano con los códigos de los accesos asignados al usuario.
   */
  setupUserAccess() {
    if (!this.menus) {
      throw Error('Menus not loaded yet.');
    }

    let userAccess = this.menus
      .map((el) => el.get('codigo'))
      .reduce((obj, menu) => Object.assign(obj, { [menu]: true }), {});

    this.user.set('access', userAccess);
  }

  /**
   * letruye un objeto plano con los códigos de los permisos asignados al usuario.
   */
  setupUserPermissions() {
    if (!this.permissions) {
      throw Error('Permissions not loaded yet.');
    }

    let userPermissions = this.permissions
      .map((el) => el.get('codigo'))
      .reduce(
        (obj, permission) => Object.assign(obj, { [permission]: true }),
        {},
      );

    this.user.set('permissions', userPermissions);
  }
}
